import { ShieldCheck, Layers, Sparkles, MapPin } from "lucide-react";
import { Container, PageHero, CtaBand } from "./_ui";
import { Seo } from "@/components/marketing/Seo";

const VALUES = [
  { icon: Layers, title: "Ryddighet", desc: "Eierskap, beslutninger og tall skal henge sammen — ikke ligge spredt i innboks og regneark." },
  { icon: ShieldCheck, title: "Trygghet", desc: "Styredokumenter er sensitive. Alt lagres kryptert i Norge, og bare styret ser styrets dokumenter." },
  { icon: Sparkles, title: "Enkelhet", desc: "Et nytt styremedlem skal være i gang samme dag — uten kurs og uten manual." },
  { icon: MapPin, title: "Norsk i bunn", desc: "Bygget på aksjeloven, med data i Norge og integrasjoner mot Brønnøysund, Proff og Altinn." },
];

export default function Om() {
  return (
    <>
      <Seo title="Om Murphy" description="Murphy Styrerom er bygget i Norge for norske styrer. Vår historie, verdiene våre og hvorfor vi bygger styrerommet vi selv savnet." path="/om" />
      <PageHero label="Om oss" title="Vi bygger styrerommet vi selv savnet.">
        Murphy Styrerom er laget for norske styrer — av folk som har sittet i dem. Målet er enkelt: at alt som kan gå galt i styrearbeidet, ikke trenger å gå galt.
      </PageHero>

      <Container className="py-10">
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <h2 className="font-headings text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl">Historien</h2>
            <div className="mt-5 space-y-4 text-base leading-relaxed text-neutral-600">
              <p>
                Protokoller på e-post, aksjeeierbok i Excel og nøkkeltall som var tre måneder gamle. Vi kjente igjen problemet fra styrerom etter styrerom: informasjonen som skal ligge til grunn for en beslutning, henger sjelden sammen.
              </p>
              <p>
                Murphy Styrerom samler det som faktisk må henge sammen — eierskap, beslutninger og tall — på ett trygt sted. Fra første protokoll til neste emisjon, for ett selskap eller en hel portefølje.
              </p>
              <p>
                Vi holder til i Lillestrøm og bygger for norske forhold: aksjeloven, BankID, norsk datalagring og direkte kobling mot regnskapssystemet.
              </p>
            </div>
          </div>
          <div className="overflow-hidden rounded-[2rem]">
            <img src="/landing/styremote.jpg" alt="Styremedlemmer rundt et møtebord" className="h-full min-h-[320px] w-full object-cover" />
          </div>
        </div>
      </Container>

      <Container className="py-12 sm:py-16">
        <h2 className="font-headings text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl">Verdiene våre</h2>
        <div className="mt-10 grid gap-5 sm:grid-cols-2">
          {VALUES.map((v) => (
            <div key={v.title} className="rounded-2xl border border-neutral-200 p-7">
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-[#CDF546]"><v.icon className="h-5 w-5 text-neutral-900" strokeWidth={1.9} /></div>
              <h3 className="mt-5 font-headings text-lg font-bold text-neutral-900">{v.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-neutral-600">{v.desc}</p>
            </div>
          ))}
        </div>
      </Container>

      <Container className="py-8 sm:py-12">
        <div className="rounded-[2rem] border border-neutral-200 p-8 sm:p-12">
          <h2 className="max-w-2xl font-headings text-2xl font-bold tracking-tight text-neutral-900 sm:text-3xl">
            «Fordi alt som kan gå galt, ikke trenger å gå galt.»
          </h2>
          <p className="mt-4 max-w-2xl text-base text-neutral-600">
            Et lite, dedikert team med bakgrunn fra styrearbeid, jus, regnskap og teknologi står bak Murphy Styrerom. Vi bygger produktet tett på kundene, og setter gjerne opp styrerommet med dine egne selskaper.
          </p>
          <p className="mt-6 text-sm text-neutral-500">Murphy AS · Torvgata 25, 2000 Lillestrøm · post@murphy.no</p>
        </div>
      </Container>

      <CtaBand />
    </>
  );
}
